import { type FC, forwardRef, type ReactElement, type Ref, useMemo } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import styled, { css } from 'styled-components';

import Flex from '../../Flex';
import { useDirection, useElement } from '../../../hooks';

import type { GridBreakPointWiseValues, GridLayoutModel } from './config';
import useGridLayoutConfig from './config';
import {
  convertLtrToRtl,
  getBreakpointAndCols,
  getLastItemsInLayout,
  getRowHeight,
  useGridItemSorter,
  useMonitoredSize,
  type GridLayoutRowsProp
} from './utils';
import ReactGridLayoutStyles from './ReactGridLayoutStyles';

const StyledWrapper = styled(Flex)`
  block-size: 100%;
`;

const StyledEmptyFooter = styled.div`
  block-size: ${({ theme }) => `calc(${theme.base.spacing} * 2)`};
`;

const StyledAutoSizeItem = styled(Flex)<{ autoSize: boolean }>(({ autoSize }) => {
  if (!autoSize) return '';
  return css`
    /* Overriding the inline style added by the RGL plugin. Needed to give the item a free height. */
    height: auto !important;

    /* Needed to maintain the extra gap at the bottom, since it's position absolute-ly */
    padding-bottom: 1rem;
  `;
});

/**
 * Wrapper on top of GridItems to forward ref, etc.
 * https://github.com/react-grid-layout/react-grid-layout#custom-child-components-and-draggable-handles
 */
const ViewerGridItem = forwardRef(
  ({ children, autoSize, direction, ...restProps }: any, ref: Ref<HTMLDivElement>) => (
    <StyledAutoSizeItem
      autoSize={autoSize}
      container={{ direction: 'column' }}
      ref={ref}
      {...restProps}
      dir={direction}
    >
      {children}
    </StyledAutoSizeItem>
  )
);

/** Props for the GridLayoutViewer component */
export interface GridLayoutViewerProps {
  /**
   * Layout-model saved previously from a GridLayoutEditor.
   * If undefined, the elements will be rendered in some default layout.
   * NOTE: A remount is needed if this layoutModel needs to be changed from the outside.
   * - The component reads it only once on load and populates layouts for missing breakpoints, assuming `lg` as the baseline
   */
  layoutModel: GridLayoutModel | undefined;

  /**
   * Breakpoints set on the Grid.
   * Needs to be aligned with `rows` and `cols`
   */
  breakpoints: GridBreakPointWiseValues;

  /**
   * # of rows to fit into the container height at each breakpoint (unless a fixed rowHeight is preferred)
   * rows should be aligned with `breakpoints` prop
   */
  rows: GridLayoutRowsProp;

  /**
   * Breakpoint-wise numColumns on the Grid.
   * cols should be aligned with `breakpoints` prop
   */
  cols: GridBreakPointWiseValues;

  /** Child elements to be rendered as Grid items. Each element should have a unique `key` */
  children?: ReactElement[];

  /**
   * List of items that needs to have free-flowing height.
   * NOTE: This works just for the items which don't have any other content underneath them.
   * By default, all items will have fixed heights derived from the `layoutModel` prop
   */
  autoSizeItemIDs?: string[];

  /**
   * Add container margin to viewer and runtime
   * @default true
   */
  padContent?: boolean;

  /**
   * Optional minimum row height (px) to override the built-in defaults.
   * Useful when grid items contain content that requires more vertical space
   * than the default minimum (e.g. compact SimpleValue insights).
   */
  minRowHeight?: number;
}

/**
 * Viewer component for a Grid created using GridLayoutEditor
 * @component
 */
const GridLayoutViewer: FC<GridLayoutViewerProps> = ({
  children,
  layoutModel: layoutModelProp,
  rows,
  cols,
  autoSizeItemIDs = [],
  breakpoints,
  padContent = true,
  minRowHeight: minRowHeightProp
}) => {
  // Track width changes: https://github.com/react-grid-layout/react-grid-layout#providing-grid-width
  const [wrapperEl, setWrapperEl] = useElement<HTMLElement>();
  const size = useMonitoredSize(wrapperEl);
  const width = size?.width;
  const height = size?.height;
  const commonGridConfig = useGridLayoutConfig(padContent);
  const { breakpoint, numColumns } = useMemo(
    () => getBreakpointAndCols(width || 0, breakpoints, cols),
    [width, breakpoints, cols]
  );
  const { rowHeight } = useMemo(
    () =>
      getRowHeight(
        breakpoint,
        height || 0,
        commonGridConfig.margin[0],
        rows,
        layoutModelProp,
        minRowHeightProp
      ),
    [breakpoint, commonGridConfig.margin, height, rows, layoutModelProp, minRowHeightProp]
  );

  // On load, setting layoutModel based on the direction
  const { rtl } = useDirection();
  const layoutModel = useMemo(() => {
    if (rtl && layoutModelProp) return convertLtrToRtl(layoutModelProp, cols);
    return layoutModelProp;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback to sort child DOM nodes to ensure an intuitive tabbing order
  const gridItemSorter = useGridItemSorter(layoutModelProp?.[breakpoint], numColumns);

  // Get list of last items in layout
  const lastItemsInLayout = useMemo(
    () => getLastItemsInLayout(layoutModel?.[breakpoint]),
    [breakpoint, layoutModel]
  );

  return (
    // Enforcing ltr here due to limitations of the RGL plugin.
    // The layout is mirrored using convertLtrToRtl above,
    // and then the direction is explicitly passed to items below.
    <StyledWrapper ref={setWrapperEl} dir='ltr'>
      <ReactGridLayoutStyles />

      {!!width && !!height && (
        <ResponsiveGridLayout
          {...commonGridConfig}
          width={width}
          rowHeight={Math.max(rowHeight, 50)}
          layouts={layoutModel}
          isResizable={false}
          isDraggable={false}
          isDroppable={false}
          cols={cols}
          breakpoints={breakpoints}
        >
          {children?.sort(gridItemSorter).map(child => {
            const autoSize =
              autoSizeItemIDs.includes(child.key!) && lastItemsInLayout.includes(child.key!);

            return (
              <ViewerGridItem key={child.key} autoSize={autoSize} direction={rtl ? 'rtl' : 'ltr'}>
                {child}
              </ViewerGridItem>
            );
          })}

          {(!children || children.length === 0) && (
            // Placeholder when no children. Needed to avoid exceptions:
            // https://github.com/react-grid-layout/react-grid-layout/issues/216
            <div key='--' />
          )}
        </ResponsiveGridLayout>
      )}
      {/** Removed container padding, hence adding extra space at bottom of grid */}
      <StyledEmptyFooter />
    </StyledWrapper>
  );
};

export default GridLayoutViewer;
